import * as React from "react";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { Dialog, Link, Typography, Box, Chip } from "@material-ui/core";
import { styled } from "@material-ui/core/styles";
import ScheduleIcon from "@material-ui/icons/Schedule";
import { InfluencerEvent } from "../../domain";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
}));

const NonLiveEventBox = styled(Box)(({ theme }) => ({
  opacity: 0.85,
  paddingInlineStart: theme.spacing(1.5),
  borderInlineStart: `2px solid ${theme.palette.grey.mid}`,
  "& .MuiTypography-h6": {
    fontWeight: theme.typography.normal,
    color: theme.palette.grey.midDark || theme.palette.text.secondary,
  },
}));

const NonLiveChip = styled(Chip)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
  backgroundColor: theme.palette.grey.lighter,
  color: theme.palette.grey.midDark || theme.palette.text.secondary,
  fontWeight: theme.typography.semiBold,
  fontSize: theme.typography.pxToRem(11),
  height: theme.spacing(2.75),
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  color: theme.palette.grey.mid,
  marginTop: theme.spacing(1),
}));

function EventDate({ influencerEvent }) {
  if (influencerEvent.startDate) {
    return (
      <Typography variant="body2" color="textSecondary">
        {format(influencerEvent.startDate, "MMMM do, yyyy")}
        {" - "}
        {format(influencerEvent.endDate, "MMMM do, yyyy")}
        {influencerEvent.platform && ` on ${influencerEvent.platform}`}
        {influencerEvent.hasInfluencer &&
          ` with ${influencerEvent.influencerName}`}
      </Typography>
    );
  }

  return (
    <Typography variant="body2" color="textSecondary">
      {format(influencerEvent.value, "MMMM do, yyyy")}
      {influencerEvent.hasTime ? " at " : ""}
      {influencerEvent.hasTime
        ? `${format(influencerEvent.value, "h:mm a")}${" "}`
        : ""}
      {influencerEvent.hasTime ? influencerEvent.browserTimezone : ""}
      {influencerEvent.platform && ` on ${influencerEvent.platform}`}
      {influencerEvent.hasInfluencer &&
        ` with ${influencerEvent.influencerName}`}
    </Typography>
  );
}

EventDate.propTypes = {
  influencerEvent: PropTypes.instanceOf(InfluencerEvent),
};

export default function AllEventsModal({ influencerEvents, open, onClose }) {
  const [isOpen, setIsOpen] = React.useState(!!open);

  const { liveEvents, nonLiveEvents } = React.useMemo(() => {
    const live = [];
    const nonLive = [];
    for (const evt of influencerEvents ?? []) {
      if (InfluencerEvent.isNonLiveSigning(evt)) {
        nonLive.push(evt);
      } else {
        live.push(evt);
      }
    }
    return { liveEvents: live, nonLiveEvents: nonLive };
  }, [influencerEvents]);

  const renderLiveEvent = (influencerEvent) => (
    <Box key={influencerEvent.influencerEventId}>
      <Typography variant="h6">
        {influencerEvent.eventTitle ?? "Event"}
      </Typography>

      <EventDate influencerEvent={influencerEvent} />

      {Boolean(influencerEvent.eventDescription) && (
        <Typography>{influencerEvent.eventDescription}</Typography>
      )}

      {Boolean(influencerEvent.url) && (
        <Link
          href={influencerEvent.url}
          target="_blank"
          rel="noopener noreferrer"
          title={influencerEvent.eventUrl}
        >
          Go to Event
        </Link>
      )}
    </Box>
  );

  const renderNonLiveEvent = (influencerEvent) => (
    <NonLiveEventBox key={influencerEvent.influencerEventId}>
      <NonLiveChip
        size="small"
        icon={<ScheduleIcon aria-hidden="true" />}
        label="Not a livestream"
      />
      <Typography variant="h6">
        {influencerEvent.eventTitle ?? "Event"}
      </Typography>

      <EventDate influencerEvent={influencerEvent} />

      <Typography variant="caption" color="textSecondary">
        Orders are fulfilled off-camera as a batch; the date shown is the order
        deadline.
      </Typography>

      {Boolean(influencerEvent.eventDescription) && (
        <Typography>{influencerEvent.eventDescription}</Typography>
      )}
    </NonLiveEventBox>
  );

  return (
    <StyledDialog
      open={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
      TransitionProps={{
        onExited: () => {
          if (!isOpen) {
            onClose();
          }
        },
      }}
    >
      <Typography variant="h4">All Events</Typography>

      {liveEvents.map(renderLiveEvent)}

      {nonLiveEvents.length > 0 && (
        <>
          <SectionLabel>Order-only — not a livestream</SectionLabel>
          {nonLiveEvents.map(renderNonLiveEvent)}
        </>
      )}
    </StyledDialog>
  );
}

AllEventsModal.propTypes = {
  influencerEvents: PropTypes.arrayOf(PropTypes.instanceOf(InfluencerEvent)),
  open: PropTypes.bool,
  onClose: PropTypes.func,
};
